import {
  DataGrid,
  type GridColDef,
  type GridFilterModel,
  type GridRowParams,
  type GridSortModel,
} from '@mui/x-data-grid'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import type { Appointment, AppointmentStatus } from '../../../types.ts'

import { getColumns } from './columns.tsx'
import { style } from './style.ts'

import type { OsaEnvironment } from '@/shared/types'

interface AppointmentTableProps {
  appointments: Appointment[]
  loading?: boolean
  total?: number
  page?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  currentStatus?: AppointmentStatus
  envFilter?: OsaEnvironment | undefined
  onEnvFilterChange?: (env: OsaEnvironment | undefined) => void
  typeFilter?: string | undefined
  onTypeFilterChange?: (type: string | undefined) => void
  subtypeFilter?: string[] | undefined
  onSubtypeFilterChange?: (subtypes: string[] | undefined) => void
  funnelFilter?: string | undefined
  onFunnelFilterChange?: (funnel: string | undefined) => void
  sortField?: string
  sortOrder?: 'asc' | 'desc'
  onSortChange?: (field: string, order: 'asc' | 'desc') => void
}

export const AppointmentTable: React.FC<AppointmentTableProps> = ({
  appointments,
  loading = false,
  total = 0,
  page = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  currentStatus = 'new',
  envFilter,
  onEnvFilterChange,
  typeFilter,
  onTypeFilterChange,
  subtypeFilter,
  onSubtypeFilterChange,
  funnelFilter,
  onFunnelFilterChange,
  sortField,
  sortOrder,
  onSortChange,
}) => {
  const navigate = useNavigate()

  const columns: GridColDef<Appointment>[] = useMemo(() => {
    return getColumns({
      currentStatus,
      typeFilter,
      onTypeFilterChange,
      subtypeFilter,
      onSubtypeFilterChange,
      funnelFilter,
      onFunnelFilterChange,
    })
  }, [
    currentStatus,
    typeFilter,
    onTypeFilterChange,
    subtypeFilter,
    onSubtypeFilterChange,
    funnelFilter,
    onFunnelFilterChange,
  ])

  // Create sort model from current sort state
  const sortModel: GridSortModel = useMemo(() => {
    if (sortField && sortOrder) {
      return [{ field: sortField, sort: sortOrder }]
    }
    return []
  }, [sortField, sortOrder])

  // Handle sort model changes
  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0]
        if (sort) {
          onSortChange?.(field, sort)
        }
      } else {
        // Clear sorting
        onSortChange?.('', 'desc')
      }
    },
    [onSortChange]
  )

  // Handle environment filter changes through DataGrid filter model
  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      const envFilterItem = model.items.find(item => item.field === 'environment')

      if (envFilterItem && envFilterItem.value) {
        const selectedEnv = envFilterItem.value as OsaEnvironment
        onEnvFilterChange?.(selectedEnv)
      } else {
        onEnvFilterChange?.(undefined)
      }
    },
    [onEnvFilterChange]
  )

  // Build filter model from current environment filter
  const filterModel: GridFilterModel = useMemo(() => {
    const items = []

    if (envFilter) {
      items.push({
        field: 'environment',
        operator: 'is',
        value: envFilter,
      })
    }

    return { items }
  }, [envFilter])

  const handleRowClick = (params: GridRowParams<Appointment>) => {
    navigate(`/osa-scheduling/show/${params.row.uuid}`)
  }

  return (
    <DataGrid
      rows={appointments}
      columns={columns}
      loading={loading}
      pageSizeOptions={[10, 25, 50]}
      density="comfortable"
      paginationMode="server"
      rowCount={total}
      getRowId={row => row.uuid}
      paginationModel={{
        page: (page || 1) - 1, // DataGrid uses 0-based pagination
        pageSize: pageSize || 10,
      }}
      onPaginationModelChange={model => {
        if (model.page !== (page || 1) - 1) {
          onPageChange?.(model.page + 1) // Convert back to 1-based
        }
        if (model.pageSize !== pageSize) {
          onPageSizeChange?.(model.pageSize)
        }
      }}
      disableColumnSelector
      onRowClick={handleRowClick}
      disableRowSelectionOnClick
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={handleSortModelChange}
      filterMode="server"
      filterModel={filterModel}
      onFilterModelChange={handleFilterModelChange}
      sx={style}
    />
  )
}
